import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../context/AuthContext';
import videoService from '../../service/video.service';
import { VideoEntity } from '../../types/entities';
import storyRunService from '../../service/story-run.service';
import storyBaseService from '../../service/story-base.service';

const YourVideos: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = React.useState<VideoEntity[] | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [thumbMap, setThumbMap] = React.useState<Record<string, string>>({});
  const [titleMap, setTitleMap] = React.useState<Record<string, string>>({});
  const [playing, setPlaying] = React.useState<Record<string, boolean>>({});
  const [hasActiveJob, setHasActiveJob] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const searchTimeoutRef = React.useRef<any>(null);
  const finishTimerRef = React.useRef<any>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<VideoEntity | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState<boolean>(false);

  const loadAll = React.useCallback(async (search = '') => {
    let alive = true;
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await videoService.getVideosByMember(user.id, 1, 10, search);
      const data = (res && res.items) || [];
      if (!alive) return;
      const sorted = (data || []).slice().sort((a: VideoEntity, b: VideoEntity) => {
        const ta = new Date(a.createdAt).getTime();
        const tb = new Date(b.createdAt).getTime();
        return tb - ta;
      });
      setItems(sorted);
      setPage(res?.page || 1);
      setHasMore(!!res?.hasMore);
      try {
        const active = await videoService.getActiveRenderJobByMember(user.id);
        if (alive) setHasActiveJob(!!active?.exists);
      } catch {
        if (alive) setHasActiveJob(false);
      }
      const runsCache: Record<string, any> = {};
      const basesCache: Record<string, any> = {};      
      const nextThumbMap: Record<string, string> = {};
      const nextTitleMap: Record<string, string> = {};
      for (const v of sorted) {
        const runId = v.storyRunId || '';
        let baseImageUrl = '';
        let baseId = v.storyBaseId || '';
        if (runId) {
          try {
            const run = runsCache[runId] || (runsCache[runId] = await storyRunService.getRunById(runId));
            baseImageUrl = String(run?.baseImageUrl || '');
            baseId = String(run?.storyBaseId || baseId || '');
          } catch {}
        }
        if (baseId) {
          try {
            const base = basesCache[baseId] || (basesCache[baseId] = await storyBaseService.getStoryBase(baseId));
            nextTitleMap[v.id] = String(base?.title || '');
          } catch {
            nextTitleMap[v.id] = '';
          }
        } else {
          nextTitleMap[v.id] = '';
        }
        if (baseImageUrl) {
          nextThumbMap[v.id] = baseImageUrl;
        }
      }
      setThumbMap(nextThumbMap);
      setTitleMap(nextTitleMap);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Debounced search: inputtan 2 saniye sonra otomatik istek
  React.useEffect(() => {
    if (!user?.id) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadAll(searchQuery);
      setPage(1);
    }, 2000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [searchQuery, loadAll, user?.id]);

  const handleLoadMore = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const res = await videoService.getVideosByMember(user.id, nextPage, 10, searchQuery);
      const data = (res && res.items) || [];
      const merged = ([...(items || []), ...data] as VideoEntity[]).slice().sort((a: VideoEntity, b: VideoEntity) => {
        const ta = new Date(a.createdAt).getTime();
        const tb = new Date(b.createdAt).getTime();
        return tb - ta;
      });
      const runsCache: Record<string, any> = {};
      const basesCache: Record<string, any> = {};
      const nextThumbMap: Record<string, string> = { ...thumbMap };
      const nextTitleMap: Record<string, string> = { ...titleMap };
      for (const v of data) {
        const runId = v.storyRunId || '';
        let baseImageUrl = '';
        let baseId = v.storyBaseId || '';
        if (runId) {
          try {
            const run = runsCache[runId] || (runsCache[runId] = await storyRunService.getRunById(runId));
            baseImageUrl = String(run?.baseImageUrl || '');
            baseId = String(run?.storyBaseId || baseId || '');
          } catch {}
        }
        if (baseId) {
          try {
            const base = basesCache[baseId] || (basesCache[baseId] = await storyBaseService.getStoryBase(baseId));
            nextTitleMap[v.id] = String(base?.title || '');
          } catch {
            nextTitleMap[v.id] = '';
          }
        } else {
          nextTitleMap[v.id] = '';
        }
        if (baseImageUrl) {
          nextThumbMap[v.id] = baseImageUrl;
        }
      }
      setItems(merged);
      setThumbMap(nextThumbMap);
      setTitleMap(nextTitleMap);
      setPage(res?.page || nextPage);
      setHasMore(!!res?.hasMore);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    let timer: any;
    const tick = async () => {
      if (!user?.id) return;
      try {
        const res = await videoService.getActiveRenderJobByMember(user.id);
        const exists = !!res?.exists;
        if (hasActiveJob && !exists) {
          if (finishTimerRef.current) {
            clearTimeout(finishTimerRef.current);
            finishTimerRef.current = null;
          }
          finishTimerRef.current = setTimeout(async () => {
            await loadAll();
            setHasActiveJob(false);
            finishTimerRef.current = null;
          }, 10000);
        } else if (!hasActiveJob && exists) {
          setHasActiveJob(true);
        }
      } catch {}
    };
    tick();
    timer = setInterval(tick, 5000);
    return () => {
      if (timer) clearInterval(timer);
      if (finishTimerRef.current) {
        clearTimeout(finishTimerRef.current);
        finishTimerRef.current = null;
      }
    };
  }, [user?.id, hasActiveJob, loadAll]);

  const handlePlay = (id: string) => {
    setPlaying((prev) => ({ ...prev, [id]: true }));
  };

  const handleDownload = async (v: VideoEntity) => {
    try {
      const res = await fetch(v.videoUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const base = (titleMap[v.id] || 'video').toString().trim() || 'video';
      const ts = new Date(v.createdAt).toISOString().replace(/[:.]/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${base}-${ts}.mp4`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
    } catch {}
  };

  const handleDeleteClick = (v: VideoEntity) => {
    setDeleteTarget(v);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await videoService.deleteVideoByUrl(deleteTarget.videoUrl);
      setItems((prev) => (prev || []).filter((x) => x.id !== deleteTarget.id));
      setThumbMap((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      setTitleMap((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      setPlaying((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Video silinirken hata oluştu');
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search videos..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Box>
      {loading && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            display: 'grid',
            gap: 2,
            boxSizing: 'border-box',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Box key={i}>
              <Card>
                <Skeleton variant="rectangular" height={160} />
                <CardContent>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && items && items.length === 0 && (
        <Alert severity="info">Henüz videon yok.</Alert>
      )}
      {!loading && !error && (
        <>
          {hasActiveJob && (
            <Box sx={{ mb: 2 }}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Videonuz oluşturuluyor...</Typography>
                </CardContent>
              </Card>
            </Box>
          )}
          {items && items.length > 0 && (
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                display: 'grid',
                gap: 2,
                boxSizing: 'border-box',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
              }}
            >
              {items.map((v, index) => {
                const thumb = thumbMap[v.id];
                const isPlaying = !!playing[v.id];
                const title = titleMap[v.id] || 'Video';
                const displayOrder = typeof v.orderNo === 'number' && v.orderNo > 0 ? v.orderNo : undefined;
                return (
                  <Box key={v.id} sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <Card sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                      {/* Show thumbnail (baseImageUrl). On click, replace with video player. */}
                      {isPlaying ? (
                        <CardMedia
                          component="video"
                          src={v.videoUrl}
                          controls
                          preload="none"
                          autoPlay
                          sx={{ width: '100%', height: 160 }}
                        />
                      ) : (
                        <CardActionArea onClick={() => handlePlay(v.id)}>
                          <CardMedia
                            component="img"
                            image={thumb || 'https://via.placeholder.com/640x360?text=Video'}
                            alt={title}
                            sx={{ width: '100%', height: 160, objectFit: 'cover' }}
                          />
                        </CardActionArea>
                      )}
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          title={title}
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {title || 'Video'}
                        </Typography>
                        {displayOrder !== undefined && (
                          <Typography variant="subtitle1" noWrap>{displayOrder}</Typography>
                        )}
                       
                        <Typography variant="caption" color="text.secondary">
                          {new Date(v.createdAt).toLocaleString()}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(v)}
                            aria-label="Delete"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDownload(v)} aria-label="Download">
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          )}
          {items && items.length > 0 && hasMore && !loading && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button variant="outlined" onClick={handleLoadMore}>Sonrakileri getir</Button>
            </Box>
          )}
        </>
      )}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} fullWidth maxWidth="xs">
        <DialogTitle>Videoyu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget
              ? `${titleMap[deleteTarget.id] || 'Video'} adlı videoyu silmek istediğinizden emin misiniz?`
              : 'Bu videoyu silmek istediğinizden emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Vazgeç
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleteLoading}>
            {deleteLoading ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YourVideos;
